import { Button, Kind } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { locationsApi } from 'api/locations'
import InfoBanner from 'components/banners/InfoBanner'
import PageHeader from 'components/PageHeader'
import { useOrderingAccess } from 'hooks/useOrderingAccess'
import { buildTranslate } from 'locales'
import React, { useMemo, useState } from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import { useDisplayError } from 'utilities/displayError'
import HealthSummaryPanel from './HealthSummaryPanel'
import LocationsTab from './LocationsTab'
import type { ConsolidatedLocation } from './mockLocations'
import { toConsolidatedLocation } from './normalisers'

const t = buildTranslate('locations.location_list')

// Mounted at `/locations` by the Locations scene wrapper when the
// `operations-center` flag is on. The wrapper enforces flag dispatch and
// `access` Locations RBAC via ProtectedRoute, so this component trusts both
// gates and renders the page directly. The Menu errors surface was split
// out to its own route at `/pos_configurations` per the 2026-05-01 thread +
// Figma 432:2419 redesign, so this page now renders the locations table
// without a tab nav. Selection + mutation state live here so the
// bulk-action buttons can sit in the PageHeader's menu slot per Figma
// 432:2292; LocationsTab receives the table-presentational state and
// renders the rows.
const Locations = () => {
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [statusFilter, setStatusFilter] = useState<'errors' | 'notices' | null>(
    null
  )
  // Locations (with the BE's reasons) that couldn't be enabled on the last bulk
  // action. Shown as an inline notice under the header buttons rather than a
  // global flash — the flash lives in the fixed <header> and overlays banners.
  const [enableFailures, setEnableFailures] = useState<
    Array<{ id: number; name: string; reasons: string[] }>
  >([])
  // Inline "some/all selected locations were skipped because they aren't
  // configured for ordering" notice (client-side filtered before the PUT).
  const [skippedMessage, setSkippedMessage] = useState<string | null>(null)

  // Non-Toast merchants don't expose location-level ordering toggles, so
  // batch actions, multi-select, the ordering-status column, and the row
  // overflow menu all hide per Figma. The Locations table is still
  // useful as a read-only directory.
  const { supportsOrderingManagement: isToastMerchant } = useOrderingAccess()

  const { data, isLoading, isError } =
    locationsApi.useGetOrderingManagementQuery()
  const [bulkUpdate, { isLoading: bulkLoading }] =
    locationsApi.useBulkUpdateOrderingStatusMutation()

  const locations = useMemo<ConsolidatedLocation[]>(
    () => (data?.ordering_configs ?? []).map(toConsolidatedLocation),
    [data]
  )

  const displayError = useDisplayError()

  const handleBulk = async (action: 'enable' | 'disable') => {
    if (selected.size === 0) return
    // Clear any prior notices at the start of a new bulk action.
    setEnableFailures([])
    setSkippedMessage(null)
    // Drop `not_configured` rows from the payload — those have no
    // underlying provider config, so the BE PUT either 4xxs or
    // silently no-ops. Filtering on the FE keeps the batch atomic
    // (no mixed succeed/skip in one mutation) and lets us tell the
    // operator exactly how many rows were dropped.
    const configurableIds: number[] = []
    const skippedCount = Array.from(selected).reduce((skipped, id) => {
      const loc = locations.find(l => l.id === id)
      if (loc?.orderingConfigured) {
        configurableIds.push(id)
        return skipped
      }
      return skipped + 1
    }, 0)
    if (configurableIds.length === 0) {
      setSkippedMessage(t('ctas.bulk_all_skipped'))
      // Keep the selection so the operator can adjust filters and
      // try again without rebuilding the set.
      return
    }
    try {
      const result = await bulkUpdate({
        action,
        locationIds: configurableIds,
      }).unwrap()
      // Only clear the selection on success — otherwise an operator
      // who hit a 5xx loses the set they spent building and has no
      // way to retry the same batch.
      setSelected(new Set())
      if (skippedCount > 0) {
        // react-redux-i18n doesn't auto-pluralize flat _one/_other keys, so pick
        // the suffix explicitly — otherwise the message resolves blank.
        setSkippedMessage(
          skippedCount === 1
            ? t('ctas.bulk_partial_skipped_one', { count: skippedCount })
            : t('ctas.bulk_partial_skipped_other', { count: skippedCount })
        )
      }
      // The BE returns 200 but skips locations it can't enable (e.g. delivery
      // is on with no handler). Surface them as an inline notice under the
      // buttons so the operator isn't left thinking every location turned on.
      if (action === 'enable' && result.enable_failures?.length) {
        setEnableFailures(
          result.enable_failures.map(f => ({
            id: f.location_id,
            name:
              locations.find(l => l.id === f.location_id)?.name ??
              t('ctas.location_fallback', { id: f.location_id }),
            reasons: f.reasons,
          }))
        )
      }
    } catch (e) {
      displayError(e, t('ctas.bulk_failed'))
    }
  }

  const handleToggleOrdering = async (loc: ConsolidatedLocation) => {
    // Clear any prior notices at the start of a new toggle.
    setEnableFailures([])
    setSkippedMessage(null)
    try {
      const result = await bulkUpdate({
        action: loc.orderingEnabled ? 'disable' : 'enable',
        locationIds: [loc.id],
      }).unwrap()
      // The BE returns 200 but skips a location it can't enable (e.g. delivery
      // is on with no handler). This per-row toggle is the third enable surface;
      // reuse the same inline notice the bulk action uses so it isn't a silent
      // no-op while the others report.
      const failure = result.enable_failures?.[0]
      if (!loc.orderingEnabled && failure) {
        setEnableFailures([
          { id: loc.id, name: loc.name, reasons: failure.reasons },
        ])
      }
    } catch (e) {
      displayError(e, t('ctas.toggle_failed'))
    }
  }

  const handleSelectBucket = (bucket: 'errors' | 'notices') => {
    setStatusFilter(prev => (prev === bucket ? null : bucket))
  }

  const headerMenu = isToastMerchant ? (
    <div>
      <Button
        kind={Kind.SECONDARY}
        className="mr-m"
        disabled={selected.size === 0 || bulkLoading}
        onClick={() => handleBulk('enable')}
      >
        {t('ctas.enable_selected')}
      </Button>
      <Button
        kind={Kind.PRIMARY}
        disabled={selected.size === 0 || bulkLoading}
        onClick={() => handleBulk('disable')}
      >
        {t('ctas.disable_selected')}
      </Button>
    </div>
  ) : undefined

  return (
    <PageHeader title={t('title')} menu={headerMenu}>
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>
      <div className="grey-05-bkg pb-xl">
        <Container className="px-0">
          {skippedMessage && (
            <div className="px-l pt-l">
              <InfoBanner text={skippedMessage} showIcon />
            </div>
          )}
          {enableFailures.length > 0 && (
            <div className="px-l pt-l">
              <Notification
                kind="negative"
                size="full"
                closeable
                onClose={() => setEnableFailures([])}
              >
                <div>
                  {enableFailures.length === 1
                    ? t('ctas.enable_failures_heading_one')
                    : t('ctas.enable_failures_heading_other', {
                        count: enableFailures.length,
                      })}
                  <ul className="mb-0 mt-xs">
                    {enableFailures.map(f => (
                      <li key={f.id}>
                        {f.name}: {f.reasons.join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              </Notification>
            </div>
          )}
          {/* Design preview — Phase 2 health summary panel. No Figma
              yet (confirmed with Reno); the panel reads real merchant-
              api data, so the only thing "preview" about it is that
              the visual treatment is awaiting design sign-off. */}
          <div className="px-l pt-l">
            <HealthSummaryPanel
              locations={locations}
              activeBucket={statusFilter}
              onSelectBucket={handleSelectBucket}
            />
          </div>
          <LocationsTab
            locations={locations}
            isLoading={isLoading}
            isError={isError}
            selected={selected}
            setSelected={setSelected}
            onToggleOrdering={handleToggleOrdering}
            rowMutationLoading={bulkLoading}
            showOrderingControls={isToastMerchant}
            statusFilter={statusFilter}
          />
        </Container>
      </div>
    </PageHeader>
  )
}

export default Locations
